import { FC, JSX, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import cx from "classnames";
import moment from "moment";
import { ColumnConfig, SortDirections, wafflesKeyColumnName, WafflesPaginationProps } from "@app/shared/waffles";
import { FlyoutLink } from "@app/shared/Flyouts";
import Modal from "@app/shared/Modals/Modal";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import ProjectService from "@app/projects/projectService";
import ProviderTerminationService from "@app/provider-terminations/providerTerminationService";
import NetNewServicingProviderService from "@app/projects/servicing-provider/NetNewServicingProviderService";
import { convertDateTime } from "@app/shared/convertDateTime";
import { ProcessStatusEnum, ProviderActionsFlyoutEnum, ProviderActionsTabsEnum } from "@app/projects/network-assessment/provider-actions/enumerators";
import {
  ALL_SPECIALTIES,
  DEFAULT_PAGE_SIZE_OPTIONS,
  DEFAULT_RECORDS_PER_PAGE,
  extractSpecialtyCodes,
  getProcessedActionText,
  getRecordingCriteriaDisplay,
  getRemainActionText,
  getRuleRecordingCriteria,
  getSuccessToastMessage,
  getTabText,
  isOrganizationRule,
  netNewDisplayText
} from "@app/projects/network-assessment/provider-actions/ProviderActionsDisplayFunctions";
import { ProjectionProviderType, RecordingCriteriaEnum, ProviderActionType } from "@app/projects/enumerators";
import ProjectionProviderByEnum from "@app/projects/servicing-provider/projectionProviderByEnum";
import { NetNewProvider } from "@app/projects/results/servicing-provider/NetNewProvider";
import { NetworkServicingProviderTerminationModel } from "@app/provider-terminations/networkServicingProviderTerminationModel";
import { ProviderActionsDrawerContext } from "@app/projects/network-assessment/provider-actions/contexts/ProviderActionsDrawerContext";
import { ZipProjectContext } from "@app/projects/zip-projects/ZipProjectContext";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import ProviderActionsFilters from "@app/projects/network-assessment/provider-actions/ProviderActionsFilters";
import ProviderActionsFlyout from "@app/projects/network-assessment/provider-actions/ProviderActionsFlyout";
import { toast } from "react-toastify";
import { ProviderActionRule, ProviderActionsTableData, ProviderActionsTabProp } from "@app/projects/network-assessment/provider-actions/interfaces";
import ProjectionProvider from "@app/projects/results/projection-provider/projectionProvider";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { formatDateTime } from "@app/shared/formatDateTime";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { sortByDate } from "@app/shared/sortByDate";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { Waffles } from "@app/shared/waffles/Waffles";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { ProjectionPanelMode } from "@app/projects/projection-provider";
import { TerminatePanelMode } from "@app/projects/servicing-provider/TerminateProvidersPanel";

type ProviderActionsBaseRow = Omit<ProviderActionsTableData, 'edit'> & {
  editRenderer: (hasUnsavedChanges: boolean) => JSX.Element | null;
};

const unsavedChangesModalContent = {
  body: "Any unsaved changes will be lost. Do you wish to continue?",
  cancelButtonText: "Cancel",
  confirmButtonText: "Continue",
  title: "Unsaved Changes",
};

const ProviderActionsTab: FC<ProviderActionsTabProp> = (props: ProviderActionsTabProp) => {
  const actionsContext = useContext(ProviderActionsDrawerContext);
  const zipProjectContext = useContext(ZipProjectContext);
  const countyProjectContext = useContext(CountyProjectContext);
  const userContext = useContext(UserContext);
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);
  const organizationActionsEnabled = enabledFeatureFlags.has(Features.ENABLE_ORGANIZATION_PROVIDER_ACTIONS);
  const projectService = useRef(new ProjectService()).current;
  const providerTerminationService = useRef(new ProviderTerminationService()).current;
  const netNewServicingProviderService = useRef(new NetNewServicingProviderService()).current;
  const pendingRequests = useRef(0);
  const status = props.tabState.filters.processStatus;
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [reasonOptions, setReasonOptions] = useState<Array<{ key: string; name: string }>>([]);
  const [reasonsFailedToLoad, setReasonsFailedToLoad] = useState(false);
  const [showUndoModal, setShowUndoModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [undoModalCounts, setUndoModalCounts] = useState({ deleteCount: 0, restoreCount: 0 });
  const [removeModalCounts, setRemoveModalCounts] = useState({ multipleAccessPoints: false, multipleProjects: false });
  const [sortDirection, setSortDirection] = useState<SortDirections>(SortDirections.Desc);

  // Determine if user has permission to select checkboxes for current tab
  const canEditRules =
    (props.tab === ProviderActionsTabsEnum.Targeted && userContext.roles.includes(Roles.PROVIDER_TARGET_USER)) ||
    (props.tab === ProviderActionsTabsEnum.Terminated && userContext.roles.includes(Roles.TERM_PROVIDER_ADMIN)) ||
    (props.tab === ProviderActionsTabsEnum.Excluded && userContext.roles.includes(Roles.PROVIDER_EXCLUDE_USER));

  const recordingCriteriaOptions = useMemo((): DropdownItem<string>[] => {
    const baseOptions: DropdownItem<string>[] = Object.entries(RecordingCriteriaEnum)
      .filter(([, value]) => {
        if (!userContext.roles.includes(Roles.TIN_VIEWER)) {
          return value !== RecordingCriteriaEnum.TIN && value !== RecordingCriteriaEnum.NPIAndTIN;
        }

        return true;
      })
      .map(([key, value]) => ({ key, name: value as string }));

    if (props.tab === ProviderActionsTabsEnum.Targeted && userContext.roles.includes(Roles.NET_NEW_PROVIDER_USER) && (!actionsContext.isZipProject || enabledFeatureFlags.has(Features.ZIP_ANALYSIS_NET_NEW))) {
      baseOptions.push({ key: netNewDisplayText, name: netNewDisplayText });
    }

    return baseOptions;
  }, [props.tab, userContext.roles]);

  const specialtyOptions = useMemo(() => {
    const allRules = [...props.tabState.processedRules, ...props.tabState.unprocessedRules];
    const extracted = extractSpecialtyCodes(allRules);

    const formatted = extracted.specialties
      .sort((a, b) => a.code.localeCompare(b.code))
      .map((s) => ({
        key: s.code,
        name: `${s.name} (${s.code})`,
      }));

    const finalOptions: Array<{ key: string; name: string }> = [];

    if (extracted.hasAllSpecialtiesProvider) {
      finalOptions.push({ key: ALL_SPECIALTIES, name: ALL_SPECIALTIES });
    }

    finalOptions.push(...formatted);

    return finalOptions;
  }, [props.tabState.processedRules, props.tabState.unprocessedRules]);

  /* oxlint-disable sort-keys */
  const providerActionsColumnConfig: ColumnConfig = useMemo<ColumnConfig>(() => {
    return {
      recordingCriteria: {
        displayText: "Recording Criteria",
        minWidth: "170px",
        noSort: true,
        sizeContentToFit: true,
        testId: "provider-actions-recording-criteria-column",
      },
      name: {
        displayText: "Name",
        minWidth: "250px",
        noSort: true,
        sizeContentToFit: true,
        testId: "provider-actions-name-column",
      },
      npi: {
        displayText: "NPI",
        minWidth: "165px",
        noSort: true,
        sizeContentToFit: true,
        testId: "provider-actions-npi-column",
      },
      ccn: {
        displayText: "CCN",
        minWidth: "120px",
        noSort: true,
        sizeContentToFit: true,
        testId: "provider-actions-ccn-column",
      },
      tin: {
        displayText: "TIN",
        hidden: !userContext.roles.includes(Roles.TIN_VIEWER),
        minWidth: "165px",
        noSort: true,
        sizeContentToFit: true,
        testId: "provider-actions-tin-column",
      },
      address: {
        displayText: "Address",
        minWidth: "250px",
        noSort: true,
        sizeContentToFit: true,
        testId: "provider-actions-address-column",
      },
      specialty: {
        displayText: "Specialty (Code)",
        minWidth: "200px",
        noSort: true,
        sizeContentToFit: true,
        testId: "provider-actions-specialty-column",
      },
      reason: {
        displayText: "Reason",
        minWidth: "200px",
        noSort: true,
        sizeContentToFit: true,
        testId: "provider-actions-reason-column",
      },
      actionBy: {
        displayText: "Action By",
        minWidth: "200px",
        noSort: true,
        sizeContentToFit: true,
        testId: "provider-actions-action-by-column",
      },
      actionDate: {
        displayText: "Action Date",
        minWidth: "200px",
        sizeContentToFit: true,
        testId: "provider-actions-action-date-column",
      },
      edit: {
        displayText: "",
        minWidth: "60px",
        noSort: true,
        sizeContentToFit: true,
        testId: "provider-actions-edit-column",
      },
    };
  }, [userContext.roles]);
  /* oxlint-enable sort-keys */

  const displayedRules = useMemo(() => {
    return status === ProcessStatusEnum.Processed ? props.tabState.filteredProcessedRules : props.tabState.filteredUnprocessedRules;
  }, [props.tabState.filteredProcessedRules, props.tabState.filteredUnprocessedRules, status]);

  const emptyTableMessage = useMemo(() => {
    if (displayedRules.length === 0) {
      return `No ${status} rules available`;
    }

    return "";
  }, [displayedRules.length, status]);

  const genericActionText = useMemo(() => getTabText(props.tab), [props.tab]);
  const processedActionText = useMemo(() => `${getProcessedActionText(props.tab)} ${genericActionText}`, [props.tab, genericActionText]);
  const remainActionText = useMemo(() => getRemainActionText(props.tab), [props.tab]);

  const undoModalBody = (
    <div className="provider-actions__modal-undo-container">
      {undoModalCounts.deleteCount > 0 && (
        <p style={{ marginBottom: undoModalCounts.restoreCount > 0 ? "1rem" : "2.5rem" }}>
          {undoModalCounts.deleteCount} selected {genericActionText.toLowerCase()} unprocessed {undoModalCounts.deleteCount === 1 ? "rule has" : "rules have"} been marked to be undone. This will remove {undoModalCounts.deleteCount === 1 ? "this provider rule" : "these provider rules"} from being processed.
        </p>
      )}

      {undoModalCounts.restoreCount > 0 && (
        <p style={{ marginBottom: "2.5rem", marginTop: undoModalCounts.deleteCount > 0 ? 0 : "1rem" }}>
          {undoModalCounts.restoreCount} selected {genericActionText.toLowerCase()} unprocessed deleted {undoModalCounts.restoreCount === 1 ? "rule has" : "rules have"} been marked to be undone. If undone, the related access points will remain {remainActionText}.
        </p>
      )}

      <p className="provider-actions__modal-undo-confirm">
        <b>Do you want to continue?</b>
      </p>
    </div>
  );

  const removeModalBody = (
    <div className="provider-actions__modal-remove-container">
      {!removeModalCounts.multipleAccessPoints && (
        <p className="provider-actions__modal-msg-container">
          Each selected provider action rule affects a single access point. Removing a rule will remove it from the access point where it is currently applied.
        </p>
      )}

      {removeModalCounts.multipleAccessPoints && (
        <p className="provider-actions__modal-msg-container">
          One or more selected provider action rules have been applied to multiple access points. Removing a rule will remove it from all access points where it is currently applied.
        </p>
      )}

      {removeModalCounts.multipleProjects && (
        <p className="provider-actions__modal-msg-container">
          One or more of these rules may affect multiple projects.
        </p>
      )}

      {!userContext.roles.includes(Roles.PROJECTION_PROCESSOR) && (
        <p className="provider-actions__modal-confirm-container">
          Clicking Continue will immediately process this change.
        </p>
      )}
    </div>
  );

  const currentTabProviderKeys = useMemo(() => {
    const currentStatus = props.tabState.filters.processStatus;
    return props.tabState.selectedProviderKeys?.[currentStatus] ?? [];
  }, [props.tabState.filters.processStatus, props.tabState.selectedProviderKeys]);

  const undoButtonDisabled = useMemo(() => {
    const isUnprocessed = props.tabState.filters.processStatus === ProcessStatusEnum.Unprocessed;
    return currentTabProviderKeys.length === 0 || !isUnprocessed || reasonsFailedToLoad;
  }, [currentTabProviderKeys.length, reasonsFailedToLoad, props.tabState.filters.processStatus]);

  const removeButtonDisabled = useMemo(() => {
    const isProcessed = props.tabState.filters.processStatus === ProcessStatusEnum.Processed;
    const hasSelectedProviders = currentTabProviderKeys.length > 0;

    const hasRequiredRole =
      (props.tab === ProviderActionsTabsEnum.Targeted && userContext.roles.includes(Roles.PROVIDER_TARGET_USER)) ||
      (props.tab === ProviderActionsTabsEnum.Terminated && userContext.roles.includes(Roles.TERM_PROVIDER_ADMIN)) ||
      (props.tab === ProviderActionsTabsEnum.Excluded && userContext.roles.includes(Roles.PROVIDER_EXCLUDE_USER));

    return !isProcessed || !hasSelectedProviders || !hasRequiredRole || reasonsFailedToLoad;
  }, [currentTabProviderKeys.length, reasonsFailedToLoad, props.tab, props.tabState.filters.processStatus, userContext.roles]);

  const canUndo = props.tabState.filters.processStatus === ProcessStatusEnum.Unprocessed && !undoButtonDisabled;
  const canRemove = props.tabState.filters.processStatus === ProcessStatusEnum.Processed && !removeButtonDisabled;

  const getProvidersByKeys = useCallback((selectedKeys: string[]): ProviderActionRule[] => {
    if (selectedKeys.length === 0) {
      return [];
    }

    const currentProviders = displayedRules || [];

    return selectedKeys
      .map((key) => {
        const parts = key.split("-");
        const id = parseInt(parts[parts.length - 1], 10);
        if (isNaN(id)) {
          return null;
        }

        if (props.tab === ProviderActionsTabsEnum.Terminated) {
          return (currentProviders as NetworkServicingProviderTerminationModel[]).find((p) => p.providerTerminationProjectId === id) ?? null;
        }

        return (currentProviders as Array<ProjectionProvider | NetNewProvider>).find((p) => {
          const isNetNew = "netNewProviderProjectId" in p;
          return isNetNew ? p.netNewProviderProjectId === id : p.projectionProviderProjectId === id;
        }) ?? null;
      })
      .filter((p): p is ProviderActionRule => p !== null);
  }, [displayedRules, props.tab]);

  const { unprocessedDeleteCount, unprocessedRestoreCount } = useMemo(() => {
    let deleteCount = 0;
    let restoreCount = 0;

    const providers = getProvidersByKeys(currentTabProviderKeys) || [];
    providers.forEach((provider) => {
      const hasDeleteDate = !!provider.deleteDate;
      if (hasDeleteDate) {
        restoreCount++;
      } else {
        deleteCount++;
      }
    });

    return { unprocessedDeleteCount: deleteCount, unprocessedRestoreCount: restoreCount };
  }, [currentTabProviderKeys, getProvidersByKeys]);

  const { hasMultipleAccessPoints, hasMultipleProjects } = useMemo(() => {
    const providers = getProvidersByKeys(currentTabProviderKeys) || [];
    const multipleAccessPoints = providers.some((p) => p.allLocations === true || p.allSpecialties === true);
    const multipleProjects = providers.some((p) => "allProjects" in p && p.allProjects === true);

    return { hasMultipleAccessPoints: multipleAccessPoints, hasMultipleProjects: multipleProjects };
  }, [currentTabProviderKeys, getProvidersByKeys]);

  const onSortChange = (_data: ProviderActionsTableData[], column: keyof ProviderActionsTableData, direction: SortDirections): ProviderActionsTableData[] => {
    const effectiveSortDirection = column === "actionDate" ? direction : SortDirections.Desc;
    setSortDirection(effectiveSortDirection);
    props.setTabState((prev) => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        [prev.filters.processStatus]: {
          ...prev.pagination[prev.filters.processStatus],
          resetToFirstPage: true,
          skip: 0,
        },
      },
    }));
    return undefined;
  };

  const providerActionsBaseData = useMemo<ProviderActionsBaseRow[]>(() => {
    let data: ProviderActionsBaseRow[] = [];

    const isUnprocessed = status === ProcessStatusEnum.Unprocessed;
    const hasTermAdminRole = userContext.roles.includes(Roles.TERM_PROVIDER_ADMIN);
    const hasTargetRole = userContext.roles.includes(Roles.PROVIDER_TARGET_USER);
    const hasExcludeRole = userContext.roles.includes(Roles.PROVIDER_EXCLUDE_USER);

    data = displayedRules.map((rule) => {
      // Type guards to determine provider type (NetNew, Termination, or Projection)
      const isTermination = "providerTerminationProjectId" in rule;
      const isNetNew = "netNewProviderProjectId" in rule;
      const isProjection = "projectionProviderProjectId" in rule;
      const isTarget = isProjection && rule.projectionType === "target";
      const isExclusion = isProjection && rule.projectionType === "excludeImpact";

      let showAlertIcon = false;
      let tooltipText = "";

      if (isUnprocessed && (rule.deleteDate || (isNetNew && !rule.isTargeted))) {
        showAlertIcon = true;

        if (isTermination) {
          tooltipText = "Processed provider marked for Restoration. Undoing will revert to a processed state.";
        } else if (isProjection) {
          tooltipText = "Processed provider marked for Removal. Undoing will revert to a processed state.";
        }
      }

      const providerKey =
        isNetNew
          ? `${ProviderActionType.NetNew}-${rule.netNewProviderProjectId}`
          : isTermination
            ? `${ProviderActionType.Termination}-${rule.providerTerminationProjectId}`
            : `${rule.projectionType}-${rule.projectionProviderProjectId}`;

      let recordingCriteria: React.ReactNode = getRuleRecordingCriteria(rule);
      const isOrganizationLocked = !organizationActionsEnabled && isOrganizationRule(rule);

       const name = ((recordingCriteria === RecordingCriteriaEnum.Name
          || recordingCriteria === RecordingCriteriaEnum.NPI
          || recordingCriteria === RecordingCriteriaEnum.NPI2
          || recordingCriteria === RecordingCriteriaEnum.TIN
          || recordingCriteria === RecordingCriteriaEnum.NPIAndTIN
          || recordingCriteria === RecordingCriteriaEnum.ContractEntity
          || recordingCriteria === RecordingCriteriaEnum.CCN
          || isNetNew)
          && !!rule.displayName)
          ? rule.displayName
          : "---";

      const npi = (recordingCriteria === RecordingCriteriaEnum.NPI || recordingCriteria === RecordingCriteriaEnum.NPI2 || recordingCriteria === RecordingCriteriaEnum.NPIAndTIN || recordingCriteria === RecordingCriteriaEnum.CCN || isNetNew)
        ? rule.npi
        : "---";

      const tin = userContext.roles.includes(Roles.TIN_VIEWER) && (recordingCriteria === RecordingCriteriaEnum.TIN || recordingCriteria === RecordingCriteriaEnum.NPIAndTIN || isNetNew)
        ? rule.tin ?? "---"
        : "---";

      const address = rule.allLocations
        ? "All Locations"
        : `${rule.address}, ${rule.city}, ${rule.state}, ${rule.zip}`;

      const specialty = rule.allSpecialties
        ? ALL_SPECIALTIES
        : rule.specialtyCode;

      const reason = isTermination
        ? `${rule.reasonName}, Terminated by ${recordingCriteria}`
        : `${rule.reasonName}${rule.allProjects ? " (All Projects)" : ""}`;

      const actionBy = rule.deleteUserDisplayName ?? rule.createUserDisplayName;
      const actionDate = formatDateTime(rule.deleteDate ?? rule.createDate); // local time
      const actionDateUtc = rule.deleteDate ?? rule.createDate; // keep utc for sorting purposes

      if (showAlertIcon) {
        recordingCriteria = (
          <>
            {recordingCriteria}
            <span className="ms-3">
              <TooltipWrapper text={tooltipText} position="right">
                <QesIcon className="mb-1" iconName={QesIcons.AlertTriangleColored} description="focus or hover for a tooltip" />
              </TooltipWrapper>
            </span>
          </>
        );
      } else {
        // Required to ensure recordingCriteria is always a ReactNode, even when the alert icon is not shown
        recordingCriteria = <>{recordingCriteria}</>;
      }

      const terminatedProviderActionRule = {} as NetworkServicingProviderTerminationModel;
      const projectionProviderActionRule = {} as ProjectionProvider;
      const netNewProviderActionRule = {} as NetNewProvider;

      // If we've got a termination, update termination rule flyout content
      if (isTermination) {
        terminatedProviderActionRule.allLocations = rule.allLocations;
        terminatedProviderActionRule.allSpecialties = rule.allSpecialties;
        terminatedProviderActionRule.npi = rule.npi;
        terminatedProviderActionRule.providerTerminationProjectId = rule.providerTerminationProjectId;
        terminatedProviderActionRule.providerId = rule.providerId;
        terminatedProviderActionRule.reasonDescription = rule.reasonDescription;
        terminatedProviderActionRule.reasonName = rule.reasonName;
        terminatedProviderActionRule.terminateByNPI = rule.terminateByNPI;
        terminatedProviderActionRule.terminateByNPI2 = rule.terminateByNPI2;
        terminatedProviderActionRule.terminateByTIN = rule.terminateByTIN;
        terminatedProviderActionRule.tin = rule.tin ?? "";
        terminatedProviderActionRule.terminateByContractEntity = rule.terminateByContractEntity;
        terminatedProviderActionRule.terminateByCCN = rule.terminateByCCN;
        terminatedProviderActionRule.ccn = rule.ccn;
      }

      // If we've got a projection, update projection rule flyout content
      if (isProjection) {
        projectionProviderActionRule.allLocations = rule.allLocations;
        projectionProviderActionRule.allSpecialties = rule.allSpecialties;
        projectionProviderActionRule.allProjects = rule.allProjects;
        projectionProviderActionRule.npi = rule.npi;
        projectionProviderActionRule.projectionProviderProjectId = rule.projectionProviderProjectId;
        projectionProviderActionRule.providerId = rule.providerId;
        projectionProviderActionRule.reasonDescription = rule.reasonDescription;
        projectionProviderActionRule.reasonName = rule.reasonName;
        projectionProviderActionRule.projectedByContractEntity = rule.projectedByContractEntity;
        projectionProviderActionRule.projectedByNPI = rule.projectedByNPI;
        projectionProviderActionRule.projectedByNPI2 = rule.projectedByNPI2;
        projectionProviderActionRule.projectedByTIN = rule.projectedByTIN;
        projectionProviderActionRule.projectedByCCN = rule.projectedByCCN;
        projectionProviderActionRule.ccn = rule.ccn;
        projectionProviderActionRule.tin = rule.tin ?? "";
        projectionProviderActionRule.exclusionVerifiedSources = rule.exclusionVerifiedSources;
        projectionProviderActionRule.hasProjectionPhone = rule.hasProjectionPhone;
        projectionProviderActionRule.phone = rule.phone;
        projectionProviderActionRule.projectionProviderSettingsId = rule.projectionProviderSettingsId;
        projectionProviderActionRule.projectionType = rule.projectionType;
      }

      if (isNetNew) {
        netNewProviderActionRule.allLocations = rule.allLocations;
        netNewProviderActionRule.allSpecialties = rule.allSpecialties;
        netNewProviderActionRule.npi = rule.npi;
        netNewProviderActionRule.projectionProviderProjectId = rule.projectionProviderProjectId;
        netNewProviderActionRule.providerId = rule.providerId;
        netNewProviderActionRule.reasonDescription = rule.reasonDescription;
        netNewProviderActionRule.reasonName = rule.reasonName;
        netNewProviderActionRule.projectedByContractEntity = rule.projectedByContractEntity;
        netNewProviderActionRule.projectedByNPI = rule.projectedByNPI;
        netNewProviderActionRule.projectedByNPI2 = rule.projectedByNPI2;
        netNewProviderActionRule.projectedByTIN = rule.projectedByTIN;
        netNewProviderActionRule.tin = rule.tin ?? "";
        netNewProviderActionRule.exclusionVerifiedSources = rule.exclusionVerifiedSources;
        netNewProviderActionRule.hasProjectionPhone = rule.hasProjectionPhone;
        netNewProviderActionRule.phone = rule.phone;
        netNewProviderActionRule.projectionProviderSettingsId = rule.projectionProviderSettingsId;
        netNewProviderActionRule.projectionType = ProjectionProviderType.Target;
        netNewProviderActionRule.netNewProviderProjectId = rule.netNewProviderProjectId;
      }

      const viewOnly =
        showAlertIcon || // Unprocessed Deleted, View Only Regardless of Role
        ((isTarget || isNetNew) && props.tab === ProviderActionsTabsEnum.Targeted && !hasTargetRole) || // View Only for Target details if User does not have Target Role
        (isExclusion && props.tab === ProviderActionsTabsEnum.Excluded && !hasExcludeRole) || // View Only for Exclude details if User does not have Exclude Role
        (isTermination && props.tab === ProviderActionsTabsEnum.Terminated && !hasTermAdminRole) // View Only for Termination details if User does not have Term Admin Role

      // View stays available for organization rows; only Edit is locked
      const isEditLocked = isOrganizationLocked && !viewOnly;

      const getFlyout = (hasUnsavedChanges: boolean) => {
          if (isTermination) {
            if (props.tab === ProviderActionsTabsEnum.Terminated) {
              return terminationFlyout(viewOnly, hasUnsavedChanges);
            }
            return null;
          }

          if (isProjection) {
            if (isNetNew && props.tab === ProviderActionsTabsEnum.Targeted) {
              if (userContext.roles.includes(Roles.NET_NEW_PROVIDER_USER) && (!actionsContext.isZipProject || enabledFeatureFlags.has(Features.ZIP_ANALYSIS_NET_NEW))) {
                return netNewFlyout(viewOnly, hasUnsavedChanges);
              }
              return null;
            }

            if (isTarget && props.tab === ProviderActionsTabsEnum.Targeted) {
              return projectionFlyout(viewOnly, hasUnsavedChanges);
            }

            if (isExclusion && props.tab === ProviderActionsTabsEnum.Excluded) {
              return projectionFlyout(viewOnly, hasUnsavedChanges);
            }
          }

          return null;
      }

      const terminationFlyout = (viewOnly: boolean = false, hasUnsavedChanges: boolean = false) => (
        <FlyoutLink
          classNames={cx(
            "fw-normal p-0",
            { "disabled text-muted": isEditLocked, "text-info": !isEditLocked },
          )}
          testId="provider-actions__termination-flyout-link"
          disabled={isEditLocked}
          content={{
            body: <ProviderActionsFlyout
              isZipProject={actionsContext.isZipProject}
              npis={[]}
              onDirtyStateChange={setHasUnsavedChanges}
              selectedProviders={[]}
              terminatePanelMode={viewOnly ? TerminatePanelMode.View : TerminatePanelMode.Edit}
              termination={terminatedProviderActionRule}
              type={ProviderActionsFlyoutEnum.Termination}
            />,
            title: "Termination Details"
          }}
          modalContent={unsavedChangesModalContent}
          warnOnClose={!viewOnly && hasUnsavedChanges}
          onOpen={() => actionsContext.setIsChildFlyoutOpen(true)}
          onClose={() => {
            actionsContext.setIsChildFlyoutOpen(false);
            setHasUnsavedChanges(false);
          }}
        >
          {viewOnly ? "View" : "Edit"}
        </FlyoutLink>
      )

      const projectionFlyout = (viewOnly: boolean = false, hasUnsavedChanges: boolean = false) => (
        <FlyoutLink
          classNames={cx(
            "fw-normal p-0",
            { "disabled text-muted": isEditLocked, "text-info": !isEditLocked },
          )}
          testId="provider-actions__projection-flyout-link"
          disabled={isEditLocked}
          content={{
            body:
              <ProviderActionsFlyout
                isTarget={isTarget}
                isZipProject={actionsContext.isZipProject}
                onDirtyStateChange={setHasUnsavedChanges}
                projectionPanelMode={viewOnly ? ProjectionPanelMode.View : ProjectionPanelMode.Edit}
                projectionProvider={projectionProviderActionRule}
                rule={rule}
                type={ProviderActionsFlyoutEnum.Projection}
              />,
            title: isTarget ? "Targeted Details" : "Exclusion Details"
          }}
          modalContent={unsavedChangesModalContent}
          warnOnClose={!viewOnly && hasUnsavedChanges}
          onOpen={() => actionsContext.setIsChildFlyoutOpen(true)}
          onClose={() => {
            actionsContext.setIsChildFlyoutOpen(false);
            setHasUnsavedChanges(false);
          }}
        >
          {viewOnly ? "View" : "Edit"}
        </FlyoutLink>
      )

      const netNewFlyout = (viewOnly: boolean = false, hasUnsavedChanges: boolean = false) => (
        <FlyoutLink
          classNames={cx(
            "fw-normal p-0",
            { "text-info": true },
          )}
          testId="provider-actions__netnew-flyout-link"
          content={{
            body:
              <ProviderActionsFlyout
                isTarget={isTarget}
                isZipProject={actionsContext.isZipProject}
                onDirtyStateChange={setHasUnsavedChanges}
                projectionPanelMode={viewOnly ? ProjectionPanelMode.View : ProjectionPanelMode.Edit}
                projectionProvider={netNewProviderActionRule}
                rule={rule}
                type={ProviderActionsFlyoutEnum.NetNew}
              />,
            title: isTarget ? "Targeted Details" : "Exclusion Details"
          }}
          modalContent={unsavedChangesModalContent}
          warnOnClose={!viewOnly && hasUnsavedChanges}
          onOpen={() => actionsContext.setIsChildFlyoutOpen(true)}
          onClose={() => {
            actionsContext.setIsChildFlyoutOpen(false);
            setHasUnsavedChanges(false);
          }}
        >
          {viewOnly ? "View" : "Edit"}
        </FlyoutLink>
      )

      /* oxlint-disable sort-keys */
      return {
        [wafflesKeyColumnName]: providerKey,
        recordingCriteria,
        name,
        npi,
        ccn: rule.ccn ?? "---",
        tin,
        address,
        specialty,
        reason,
        actionBy,
        actionDate,
        actionDateUtc,
        editRenderer: (hasUnsavedChanges: boolean) => getFlyout(hasUnsavedChanges),
        checkBoxIsDisabled: !canEditRules || isOrganizationLocked
      };
      /* oxlint-enable sort-keys */
    });

    data = sortByDate(data, "actionDateUtc", sortDirection, "name");
    return data;
  }, [actionsContext.isZipProject, canEditRules, displayedRules, enabledFeatureFlags, organizationActionsEnabled, props, sortDirection, userContext.roles]);

  const providerActionsTableData: ProviderActionsTableData[] = useMemo(() => {
    return providerActionsBaseData.map(({ editRenderer, ...row }) => ({
      ...row,
      edit: editRenderer(hasUnsavedChanges),
    }));
  }, [providerActionsBaseData, hasUnsavedChanges]);

  const pagedData = useMemo(() => {
    const paginationOptions = props.tabState.pagination[status];
    const skip = paginationOptions.skip ?? 0;
    const recordsPerPage = paginationOptions.recordsPerPage ?? DEFAULT_RECORDS_PER_PAGE;
    return providerActionsTableData.slice(skip, skip + recordsPerPage);
  }, [props.tabState.pagination, providerActionsTableData, status]);

  const beginGlobalLoad = useCallback(() => {
    pendingRequests.current++;
    props.setTabState((prev) => ({ ...prev, isLoading: true }));
  }, [props.setTabState]);

  const endGlobalLoad = useCallback(() => {
    pendingRequests.current = Math.max(0, pendingRequests.current - 1);
    if (pendingRequests.current === 0) {
      props.setTabState((prev) => ({ ...prev, isLoading: false }));
    }
  }, [props.setTabState]);

  const refreshRules = async (force: boolean = false) => {
    // Only fetch if not initialized OR if forced (via refreshToken)
    if (!force && props.tabState.isInitialized) {
      return;
    }

    beginGlobalLoad();

    try {
      props.setTabState((prev) => ({ ...prev, dataLoadFailed: false }));
      let processed: ProviderActionRule[] = [];
      let unprocessed: ProviderActionRule[] = [];
      if (props.tab === ProviderActionsTabsEnum.Targeted) {
        const [
          processedProjectionRules,
          unprocessedProjectionRules,
          processedNetNewData,
          unprocessedNetNewData,
        ] = await Promise.all([
          projectService.getProcessedProjectionProvider(actionsContext.projectId, ProjectionProviderType.Target),
          projectService.getUnprocessedProjectionProvider(actionsContext.projectId, ProjectionProviderType.Target),
          userContext.roles.includes(Roles.NET_NEW_PROVIDER_USER) && (!actionsContext.isZipProject || enabledFeatureFlags.has(Features.ZIP_ANALYSIS_NET_NEW))
            ? netNewServicingProviderService.getProcessedTargetedNetNewProviders(actionsContext.projectId)
            : Promise.resolve([]),
          userContext.roles.includes(Roles.NET_NEW_PROVIDER_USER) && (!actionsContext.isZipProject || enabledFeatureFlags.has(Features.ZIP_ANALYSIS_NET_NEW))
            ? netNewServicingProviderService.getUnprocessedNetNewProviders(actionsContext.projectId)
            : Promise.resolve([]),
        ]);
        processed = [...processedProjectionRules, ...processedNetNewData];
        unprocessed = [...unprocessedProjectionRules, ...unprocessedNetNewData];
      } else if (props.tab === ProviderActionsTabsEnum.Excluded) {
        const [processedProjectionRules, unprocessedProjectionRules] = await Promise.all([
          projectService.getProcessedProjectionProvider(actionsContext.projectId, ProjectionProviderType.ExcludeImpact),
          projectService.getUnprocessedProjectionProvider(actionsContext.projectId, ProjectionProviderType.ExcludeImpact),
        ]);
        processed = processedProjectionRules;
        unprocessed = unprocessedProjectionRules;
      } else {
        const isTermViewer = userContext.roles.includes(Roles.TERM_PROVIDER_VIEWER);
        const isTermAdmin = userContext.roles.includes(Roles.TERM_PROVIDER_ADMIN);
        if (isTermViewer || isTermAdmin) {
          processed = (await providerTerminationService.getProcessedProviderTerminations(actionsContext.projectId)).filter(x => !x.deleteDate);
          if (isTermAdmin){
            unprocessed = await providerTerminationService.getUnprocessedProviderTerminations(actionsContext.projectId)
          }
        }
      }
      props.setTabState((prev) => ({
        ...prev,
        filteredProcessedRules: filterRules(processed),
        filteredUnprocessedRules: filterRules(unprocessed),
        isInitialized: true,
        processedRules: processed,
        unprocessedRules: unprocessed
      }));
    } catch {
      props.setTabState((prev) => ({ ...prev, dataLoadFailed: true }));
    } finally {
      endGlobalLoad();
    }
  }

  const loadReasons = useCallback(async () => {
    beginGlobalLoad();
    try {
      setReasonsFailedToLoad(false);

      if (props.tab === ProviderActionsTabsEnum.Targeted) {
        const reasons = await projectService.getTargetProviderReasons(actionsContext.projectId);
        setReasonOptions(reasons.map((r) => ({ key: r, name: r })));
      } else if (props.tab === ProviderActionsTabsEnum.Excluded) {
        const reasons = await projectService.getProviderExclusionReasons(actionsContext.projectId);
        setReasonOptions(reasons.map((r) => ({ key: r.reason, name: r.reason })));
      } else {
        if (userContext.roles.includes(Roles.TERM_PROVIDER_ADMIN) || userContext.roles.includes(Roles.TERM_PROVIDER_VIEWER)) {
          const reasons = await providerTerminationService.getProviderTerminationReasons();
          setReasonOptions(reasons.map((r) => ({ key: r, name: r })));
        } else {
          setReasonOptions([]);
        }
      }
    } catch {
      setReasonsFailedToLoad(true);
    } finally {
      endGlobalLoad();
    }
  }, [props.tab]);

  const filterRules = useCallback((rules: ProviderActionRule[]) => {
    const filters = props.tabState.filters;

    const normalizedSearchInput = filters.searchByInput?.trim().toLowerCase() ?? null;

    const actionFromDate = filters.actionDateFrom
      ? moment(filters.actionDateFrom).startOf("day").toDate()
      : null;
    const actionToDate = filters.actionDateTo
      ? moment(filters.actionDateTo).endOf("day").toDate()
    : null;

    return (rules || []).filter((rule) => {
      const isTermination = "providerTerminationProjectId" in rule;
      const isNetNew = "netNewProviderProjectId" in rule;

      const recordingCriteria = isTermination
        ? getRecordingCriteriaDisplay(false, rule.terminateByContractEntity, rule.terminateByNPI, rule.terminateByTIN, rule.terminateByNPI2, rule.terminateByCCN)
        : getRecordingCriteriaDisplay(isNetNew, rule.projectedByContractEntity, rule.projectedByNPI, rule.projectedByTIN, rule.projectedByNPI2, rule.projectedByCCN);

      if (filters.searchByOption === ProjectionProviderByEnum.NPI && filters.searchByInput.length > 0) {
        if (![netNewDisplayText, RecordingCriteriaEnum.NPI, RecordingCriteriaEnum.NPI2, RecordingCriteriaEnum.NPIAndTIN].includes(recordingCriteria)) {
          return false;
        }

        const matchedNpi = rule.npi?.includes(normalizedSearchInput) ?? false;
        if (!matchedNpi) {
          return false;
        }
      }

      if (filters.searchByOption === ProjectionProviderByEnum.TIN && filters.searchByInput.length > 0) {
        if (![netNewDisplayText, RecordingCriteriaEnum.TIN, RecordingCriteriaEnum.NPIAndTIN].includes(recordingCriteria)) {
          return false;
        }

        const matchedTin = rule.tin?.includes(normalizedSearchInput) ?? false;
        if (!matchedTin) {
          return false;
        }
      }

      if (filters.searchByOption === ProjectionProviderByEnum.Name && filters.searchByInput.length > 0) {
        if (![netNewDisplayText, RecordingCriteriaEnum.Name, RecordingCriteriaEnum.NPI, RecordingCriteriaEnum.NPI2, RecordingCriteriaEnum.NPIAndTIN].includes(recordingCriteria)) {
          return false;
        }

        const matchedName = rule.displayName?.toLowerCase().includes(normalizedSearchInput);
        if (!matchedName) {
          return false;
        }
      }

      if (filters.specialtyGroupCodes.length > 0) {
        const hasAllSpecialtiesFilter = filters.specialtyGroupCodes.some((selected) => selected === ALL_SPECIALTIES);
        const matchedAllSpecialties = hasAllSpecialtiesFilter && rule.allSpecialties;

        const matchesSpecialty = filters.specialtyGroupCodes.some((selected) => !rule.allSpecialties && selected === rule.specialtyCode);

        if (!matchesSpecialty && !matchedAllSpecialties) {
          return false;
        }
      }

      if (filters.reason.length > 0) {
        const matchesReason = filters.reason.some((selectedReason) => selectedReason === rule.reasonName);
        if (!matchesReason) {
          return false;
        }
      }

      if (filters.recordingCriteria.length > 0) {
        const matchesRecordingCriteria = filters.recordingCriteria.some((selected) => selected === recordingCriteria);
        if (!matchesRecordingCriteria) {
          return false;
        }
      }

      const ruleDate = convertDateTime(rule.deleteDate ?? rule.createDate);

      if (filters.actionDateFrom !== null && ruleDate < actionFromDate) {
        return false;
      }

      if (filters.actionDateTo !== null && ruleDate > actionToDate) {
        return false;
      }

      return true;
    });
  }, [props.tabState.filters]);

  const applyFilters = useCallback(() => {
      props.setTabState((prev) => ({
      ...prev,
      filteredProcessedRules: filterRules(prev.processedRules),
      filteredUnprocessedRules: filterRules(prev.unprocessedRules),
      pagination: {
        ...prev.pagination,
        [prev.filters.processStatus]: {
          ...prev.pagination[prev.filters.processStatus],
          resetToFirstPage: true,
          skip: 0
        },
      },
    }));
  }, [filterRules, props.setTabState]);

  const handleClearAll = useCallback(() => {
    props.setTabState((prev) => {
      const currentProcessStatus = prev.filters.processStatus;

      return {
        ...prev,
        filteredProcessedRules: prev.processedRules,
        filteredUnprocessedRules: prev.unprocessedRules,
        filters: {
          ...prev.filters,
          actionDateFrom: null,
          actionDateTo: null,
          processStatus: currentProcessStatus,
          reason: [],
          recordingCriteria: [],
          searchByInput: "",
          searchByOption: ProjectionProviderByEnum.Name,
          specialtyGroupCodes: []
        },

        pagination: {
          ...prev.pagination,
          [currentProcessStatus]: {
            ...prev.pagination[currentProcessStatus],
            recordsPerPage: prev.pagination[currentProcessStatus].recordsPerPage ?? DEFAULT_RECORDS_PER_PAGE,
            resetToFirstPage: true,
            skip: 0,
          },
        },
      };
    });
  }, [props.setTabState]);

  const removeProjectionRules = (
    projectId: number,
    selectedProviders: Array<ProjectionProvider | NetNewProvider>
  ): Array<Promise<void | boolean>> => {
    const netNewIdsToUntarget: number[] = [];
    const targetIdsToDelete: number[] = [];
    const targetIdsToRestore: number[] = [];

    selectedProviders.forEach((provider) => {
      if (provider.projectionType === ProjectionProviderType.Target || provider.projectionType === ProjectionProviderType.ExcludeImpact) {
        const hasDeleteDate = !!provider.deleteDate;
        if (hasDeleteDate) {
          targetIdsToRestore.push(provider.projectionProviderProjectId);
        } else {
          targetIdsToDelete.push(provider.projectionProviderProjectId);
        }
      } else {
        netNewIdsToUntarget.push((provider as NetNewProvider).netNewProviderProjectId);
      }
    });

    const promises: Array<Promise<void | boolean>> = [];

    if (netNewIdsToUntarget.length > 0) {
      promises.push(netNewServicingProviderService.updateNetNewIsTargeted(false, netNewIdsToUntarget));
    }
    if (targetIdsToDelete.length > 0) {
      promises.push(projectService.deleteProjectionProviders(projectId, targetIdsToDelete));
    }
    if (targetIdsToRestore.length > 0) {
      promises.push(projectService.restoreProjectionProviders(projectId, targetIdsToRestore));
    }

    return promises;
  };

  const removeTerminatedRules = (
    projectId: number,
    selectedProviders: NetworkServicingProviderTerminationModel[]
  ): Array<Promise<void | boolean>> => {
    const terminationIdsToDelete: number[] = [];
    const terminationIdsToRestore: number[] = [];

    selectedProviders.forEach((provider) => {
      const hasDeleteDate = !!provider.deleteDate;

      if (hasDeleteDate) {
        terminationIdsToRestore.push(provider.providerTerminationProjectId);
      } else {
        terminationIdsToDelete.push(provider.providerTerminationProjectId);
      }
    });

    const promises: Array<Promise<void | boolean>> = [];

    if (terminationIdsToDelete.length > 0) {
      promises.push(providerTerminationService.deleteTermination(projectId, terminationIdsToDelete));
    }
    if (terminationIdsToRestore.length > 0) {
      promises.push(providerTerminationService.restoreTermination(projectId, terminationIdsToRestore));
    }

    return promises;
  }

  const executeUndoOrRemove = async (selectedProviderKeys: string[]) => {
    if (selectedProviderKeys.length === 0) {
      return;
    }

    beginGlobalLoad();

    try {
      const projectId = actionsContext.projectId;

      const selectedProviders = getProvidersByKeys(selectedProviderKeys);

      let removePromises: Array<Promise<void | boolean>> = [];
      let pendingBannerReset: (() => void) | null = null;

      switch (props.tab) {
        case ProviderActionsTabsEnum.Targeted:
          if(status === ProcessStatusEnum.Unprocessed && (actionsContext.unprocessedCounts?.targeted ?? 0) - selectedProviderKeys.length === 0 && selectedProviderKeys.length === (actionsContext.unprocessedCounts?.projectionTotal ?? 0))
          {
            pendingBannerReset = () => props.resetBanner?.((actionsContext.unprocessedCounts?.total ?? 0) > (actionsContext.unprocessedCounts?.projectionTotal ?? 0), false);
          }
          removePromises = removeProjectionRules(projectId, selectedProviders as Array<ProjectionProvider | NetNewProvider>);
          break;

        case ProviderActionsTabsEnum.Excluded:
          if(status === ProcessStatusEnum.Unprocessed && (actionsContext.unprocessedCounts?.excluded ?? 0) - selectedProviderKeys.length === 0 && selectedProviderKeys.length === (actionsContext.unprocessedCounts?.projectionTotal ?? 0))
          {
            pendingBannerReset = () => props.resetBanner?.((actionsContext.unprocessedCounts?.total ?? 0) > (actionsContext.unprocessedCounts?.projectionTotal ?? 0), false);
          }
          removePromises = removeProjectionRules(projectId, selectedProviders as Array<ProjectionProvider | NetNewProvider>);
          break;

        case ProviderActionsTabsEnum.Terminated:
          if(status === ProcessStatusEnum.Unprocessed && (actionsContext.unprocessedCounts?.terminated ?? 0) - selectedProviderKeys.length === 0)
          {
            pendingBannerReset = () => props.resetBanner?.(false, (actionsContext.unprocessedCounts?.targeted ?? 0) > 0, (actionsContext.unprocessedCounts?.excluded ?? 0) > 0);
          }
          removePromises = removeTerminatedRules(projectId, selectedProviders as NetworkServicingProviderTerminationModel[]);
          break;
      }

      await Promise.all(removePromises);

      pendingBannerReset?.();
      // Clear selections for current status
      props.setTabState((prev) => ({
        ...prev,
        selectedProviderKeys: {
          ...prev.selectedProviderKeys,
          [props.tabState.filters.processStatus]: [],
        },
      }));

      const isTargetedOrExcluded = props.tab === ProviderActionsTabsEnum.Targeted || props.tab === ProviderActionsTabsEnum.Excluded;
      const isProcessed = status === ProcessStatusEnum.Processed;
      const hasManualProcessingRole = userContext.roles.includes(Roles.PROJECTION_PROCESSOR);

      // If the current process status is Processed and the user doesn't have permission to do Manual Processing, process the rules immediately
      if (isTargetedOrExcluded && isProcessed && !hasManualProcessingRole) {
        if (actionsContext.isZipProject) {
          await zipProjectContext.onProcessProviderChanges(false);
        } else {
          await countyProjectContext.processProviderChanges(false, props.shouldSkipTerminationTimeDistanceRecalc);
        }
      }
      //Force data refreshes
      actionsContext.requestRulesRefresh();
      applyFilters();

      //For syncing terminated provider action changes with PCI Dashboard provider list
      if (props.tab === ProviderActionsTabsEnum.Terminated) {
        const terminatedUnprocessedBefore = actionsContext.unprocessedCounts?.terminated ?? 0;
        const delta =
          status === ProcessStatusEnum.Unprocessed
          ? -selectedProviderKeys.length
          : selectedProviderKeys.length;
        const hasUnprocessed = terminatedUnprocessedBefore + delta > 0;
        actionsContext.notifyTerminationsChanged?.(hasUnprocessed);
      }

      toast.success(
        getSuccessToastMessage(props.tab, status, selectedProviderKeys.length), {
          autoClose: 3000,
          containerId: "z-index-9999",
          position: "top-center",
        }
      );
    } catch {
      toast.error("Action failed. Please try again.", {
        containerId: "z-index-9999",
        position: "top-center",
      });
    } finally {
      endGlobalLoad();
    }
  }

  const handleUndoClick = () => {
    setUndoModalCounts({ deleteCount: unprocessedDeleteCount, restoreCount: unprocessedRestoreCount });
    setShowUndoModal(true);
  };

  const handleRemoveClick = () => {
    setRemoveModalCounts({ multipleAccessPoints: hasMultipleAccessPoints, multipleProjects: hasMultipleProjects });
    setShowRemoveModal(true);
  };

  const handleUndoConfirm = async () => {
    setShowUndoModal(false);
    await executeUndoOrRemove(currentTabProviderKeys);
  };

  const handleRemoveConfirm = async () => {
    setShowRemoveModal(false);
    await executeUndoOrRemove(currentTabProviderKeys);
  };

  const handleCheckboxChange = (selectedKeys: string[]) => {
    const currentStatus = props.tabState.filters.processStatus;

    props.setTabState((prev) => ({
      ...prev,
      selectedProviderKeys: {
        ...prev.selectedProviderKeys,
        [currentStatus]: selectedKeys,
      },
    }));
  };

  const handlePageChange = useCallback((pageNumber: number) => {
    props.setTabState((prev) => {
      const currentStatus = prev.filters.processStatus;
      const recordsPerPage = prev.pagination[currentStatus].recordsPerPage ?? DEFAULT_RECORDS_PER_PAGE;
      return {
        ...prev,
        pagination: {
          ...prev.pagination,
          [currentStatus]: {
            ...prev.pagination[currentStatus],
            resetToFirstPage: false,
            skip: (pageNumber - 1) * recordsPerPage,
          },
        },
      };
    });
  }, [props.setTabState]);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    props.setTabState((prev) => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        [prev.filters.processStatus]: {
          ...prev.pagination[prev.filters.processStatus],
          recordsPerPage: pageSize,
          resetToFirstPage: true,
          skip: 0
        },
      },
    }));
  }, [props.setTabState]);

  const paginationProps = useMemo<WafflesPaginationProps>(() => {
    const p = props.tabState.pagination[status];
    return {
      changePage: handlePageChange,
      enableBackendPagination: false,
      handlePageSizeChange,
      pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
      recordsPerPage: p.recordsPerPage ?? DEFAULT_RECORDS_PER_PAGE,
      resetToFirstPage: p.resetToFirstPage ?? false,
      showPageSizeSelector: true,
      skip: p.skip ?? 0,
      totalRecords: p.totalRecords ?? 0
    };
  }, [handlePageChange, handlePageSizeChange, props.tabState.pagination, status]);

  const refreshToken = useMemo(() => {
    return actionsContext.rulesRefreshTokens?.[props.tab] ?? 0;
  }, [actionsContext.rulesRefreshTokens, props.tab]);

  useEffect(() => {
    props.setTabState((prev) => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        [status]: {
          ...prev.pagination[status],
          totalRecords: providerActionsTableData.length,
        },
      },
    }));
  }, [props.setTabState, providerActionsTableData.length, status]);

  // Initial load - only runs once
  useEffect(() => {
    loadReasons();
    refreshRules(false);
  }, []);

  // Force refresh when refreshToken changes (after Process All, undo, or edit actions)
  useEffect(() => {
    if (refreshToken === 0) {
      return;
    }
    refreshRules(true);
  } ,[refreshToken]);

  return (
    <div className="mt-4 provider-actions-tab-wrapper">
      <ProviderActionsFilters
        tab={props.tab}
        tabState={props.tabState}
        setTabState={props.setTabState}
        specialtyOptions={specialtyOptions}
        reasonOptions={reasonOptions}
        recordingCriteriaOptions={recordingCriteriaOptions}
        filteringCriteriaFailedToLoad={reasonsFailedToLoad}
        onClearAll={handleClearAll}
        onSearch={applyFilters}
        canUndo={canUndo}
        canRemove={canRemove}
        genericActionText={genericActionText}
        removeActionText={processedActionText}
        onUndoClick={handleUndoClick}
        onRemoveClick={handleRemoveClick}
      />

      {props.tabState.dataLoadFailed ? (
        <QESAlert type="warning">Problem retrieving rules. Please Try Again</QESAlert>
      ) : (
        <Waffles<ProviderActionsTableData>
          columns={providerActionsColumnConfig}
          customSort={onSortChange}
          data={pagedData}
          emptyTableMessage={emptyTableMessage}
          enablePagination={true}
          fullWidth={true}
          handleCheckboxChange={handleCheckboxChange}
          loading={props.tabState.isLoading || !!actionsContext.isLoading}
          paginationProps={paginationProps}
          passThroughSorting={true}
          selectedKeys={props.tabState.selectedProviderKeys?.[status] ?? []}
          showCheckboxes={canEditRules}
          sortDirectionOverride={sortDirection}
          testId="provider-actions-waffles-table"
          totalNumberOfRecords={paginationProps.totalRecords}
        />
      )}

      <Modal
        isVisible={showUndoModal}
        closeModal={() => setShowUndoModal(false)}
        modalWidth={25}
        content={{
          body: undoModalBody,
          cancelButtonText: "Cancel",
          confirmButtonAction: handleUndoConfirm,
          confirmButtonText: "Remove",
          title: `Undo ${genericActionText}`,
        }}
      />

      <Modal
        isVisible={showRemoveModal}
        closeModal={() => setShowRemoveModal(false)}
        modalWidth={25}
        content={{
          body: removeModalBody,
          cancelButtonText: "Cancel",
          confirmButtonAction: handleRemoveConfirm,
          confirmButtonText: "Continue",
          title: processedActionText
        }}
      />
    </div>
  );
};

export default ProviderActionsTab;